import {
  ActionIcon,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import { Link, useLocation } from "wouter";
import { useStore } from "../store";
import Timeago from "../../../Boilerplate/_Shared/Timeago";
import {
  IconBrandAmazon,
  IconDownload,
  IconFileSpreadsheet,
  IconFileUpload,
  IconInfoCircle,
  IconTransform,
  IconTrash,
} from "@tabler/icons-react";
import ImportStatusBadge from "./ImportStatusBadge";
import FileSize from "./FileSize";
import { deleteImport, getImports } from "../api";
import { useCallback, useEffect, useRef, useState } from "react";
import { usePolling } from "../hooks/usePolling";
import { notifications } from "@mantine/notifications";
import { confirmRemoveImport } from "./confirmRemoveImport";

function EmptyState() {
  return (
    <Container size="sm" mt="xl">
      <Stack align="center" gap="xl">
        <div style={{ textAlign: "center" }}>
          <IconTransform size={48} stroke={1} color="var(--mantine-color-dimmed)" />
          <Title order={2} mt="md">
            No imports yet
          </Title>
          <Text c="dimmed" mt="sm" maw={500}>
            Upload a spreadsheet from your webshop, another marketplace like
            eBay, your supplier, or your Amazon ASINs. We detect the file type
            and turn it into a marketplace-ready file you publish yourself.
          </Text>
        </div>

        <Button
          component={Link}
          to="/imports/upload"
          size="lg"
          color="grape"
          leftSection={<IconFileUpload size={20} />}
        >
          Upload your first file
        </Button>

        <SimpleGrid cols={{ base: 1, sm: 3 }} mt="md" w="100%">
          <Paper p="md" withBorder>
            <Stack align="center" ta="center" gap="xs">
              <ThemeIcon color="orange" variant="light" size="lg" radius="xl">
                <IconBrandAmazon size={20} />
              </ThemeIcon>
              <Text fw={500} size="sm">
                Amazon to Mirakl
              </Text>
              <Text size="xs" c="dimmed">
                Paste your ASINs and create optimised Mirakl listings from your
                Amazon data
              </Text>
            </Stack>
          </Paper>
          <Paper p="md" withBorder>
            <Stack align="center" ta="center" gap="xs">
              <ThemeIcon color="blue" variant="light" size="lg" radius="xl">
                <IconTransform size={20} />
              </ThemeIcon>
              <Text fw={500} size="sm">
                Optimise Mirakl Listings
              </Text>
              <Text size="xs" c="dimmed">
                Upload your existing Mirakl catalogue to enhance content and
                fill missing attributes
              </Text>
            </Stack>
          </Paper>
          <Paper p="md" withBorder>
            <Stack align="center" ta="center" gap="xs">
              <ThemeIcon color="teal" variant="light" size="lg" radius="xl">
                <IconFileSpreadsheet size={20} />
              </ThemeIcon>
              <Text fw={500} size="sm">
                Convert Product Data
              </Text>
              <Text size="xs" c="dimmed">
                Upload a CSV or XLSX from your webshop, eBay, or a supplier
              </Text>
            </Stack>
          </Paper>
        </SimpleGrid>
      </Stack>
    </Container>
  );
}

function ImportList() {
  const [, setLocation] = useLocation();
  const imports = useStore((state) => state.imports);
  const setImports = useStore((state) => state.setImports);
  const removedIds = useRef(new Set());
  const [deletingId, setDeletingId] = useState(null);
  const hasActiveTasks =
    Array.isArray(imports) &&
    imports.some(
      (imp) =>
        imp.status === "processing" ||
        imp.status === "pending" ||
        imp.preflightStatus === "running"
    );

  const refresh = useCallback(() => {
    getImports().then((data) => {
      if (!Array.isArray(data)) return;
      const removed = removedIds.current;
      setImports(data.filter((item) => !removed.has(item.id)));
    });
  }, [setImports]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  usePolling(refresh, {
    enabled: Array.isArray(imports),
    intervalMs: hasActiveTasks ? 5_000 : 60_000,
    invokeImmediately: false,
  });

  if (!Array.isArray(imports)) {
    return (
      <Center>
        <Loader />
      </Center>
    );
  }

  if (imports.length === 0) {
    return <EmptyState />;
  }

  const handleRemove = (importData, evt) => {
    evt.stopPropagation();
    confirmRemoveImport(importData.originalName, () => {
      setDeletingId(importData.id);
      deleteImport(importData.id)
        .then(() => {
          removedIds.current.add(importData.id);
          setImports((current) =>
            Array.isArray(current)
              ? current.filter((item) => item.id !== importData.id)
              : current,
          );
          notifications.show({ message: "Removed from your imports" });
        })
        .catch(() => {
          notifications.show({
            color: "red",
            message: "Could not remove this file. Try again.",
          });
        })
        .finally(() => setDeletingId(null));
    });
  };

  return (
    <Container size="xl">
      <Group justify="space-between">
        <Title>Imported Files</Title>
        <Button
          component={Link}
          to="/imports/upload"
          variant="default"
          leftSection={<IconFileUpload size={16} />}
        >
          Import file
        </Button>
      </Group>
      <Table
        style={{ cursor: "pointer" }}
        striped
        highlightOnHover
        tabularNums
        mt="lg"
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Created</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>File name</Table.Th>
            <Table.Th ta="right">Size</Table.Th>
            <Table.Th ta="right">
              SKUs&nbsp;
              <Tooltip label="The number of product listings successfully read from your file.">
                <IconInfoCircle size={16} stroke={1} />
              </Tooltip>
            </Table.Th>
            <Table.Th></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {imports.map((importData) => (
            <Table.Tr
              key={importData.id}
              onClick={() => setLocation(`/imports/${importData.id}`)}
            >
              <Table.Td>
                <Timeago date={importData.created} />
              </Table.Td>
              <Table.Td>
                <ImportStatusBadge status={importData.status} />
              </Table.Td>
              <Table.Td>{importData.originalName}</Table.Td>
              <Table.Td ta="right">
                <FileSize size={importData.fileSize} />
              </Table.Td>
              <Table.Td ta="right">{importData.rowCount}</Table.Td>
              <Table.Td ta="right">
                <Group gap={4} justify="flex-end" wrap="nowrap">
                  <Tooltip label="Download original file" position="bottom">
                    <ActionIcon
                      variant="subtle"
                      onClick={(evt) => {
                        evt.stopPropagation();
                      }}
                      target="_blank"
                      rel="noopener noreferrer"
                      component="a"
                      href={`https://aiteotest.blob.core.windows.net/optivise/${importData.fileStorePath}`}
                    >
                      <IconDownload size={14} stroke={1} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Remove from imports" position="bottom">
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      aria-label="Remove from imports"
                      loading={deletingId === importData.id}
                      onClick={(evt) => handleRemove(importData, evt)}
                    >
                      <IconTrash size={14} stroke={1} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Container>
  );
}

export default ImportList;
